import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject,
} from '@angular/core';

import { EventDetailsColumnType } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EventSwitcherEventItem, EventSwitcherItem, MediaEventSource } from '@frontend/sports/grid/media/feature/model';
import { IMediaRootState, MediaApiActions } from '@frontend/sports/grid/media/feature/state';
import { AdaptiveLayoutService, RightColumnSize } from '@frontend/sports/layout/feature';
import { TrackingService, TrackingVideoSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';

import { ExpansionPanelComponent } from '../common/expansion-panel/expansion-panel.component';
import { Actions } from './actions';
import { ScrollToActiveDirective } from './scroll-to-active.directive';

const containerHeightMap = {
    [RightColumnSize.ExtraLarge]: 'size-xl',
    [RightColumnSize.Large]: 'size-l',
};

@Component({
    selector: 'ms-event-selector',
    templateUrl: './event-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, IconCustomComponent, ExpansionPanelComponent, ScrollToActiveDirective],
})
export class EventSelectorComponent implements OnInit, OnChanges {
    sitecore = inject(Sitecore);
    private elementRef = inject(ElementRef);
    private trackingService = inject(TrackingService);
    private adaptiveLayoutService = inject(AdaptiveLayoutService);
    private store = inject<Store<IMediaRootState>>(Store);

    @Input() eventsList: EventSwitcherItem[];
    @Input() selectedEventId: string | null;
    @HostBinding('class') className = 'event-selector';

    collapsed = true;
    selectedEvent: { id: string; name: string; sportId: number } | null = null;
    containerHeight = '';
    selectorTitle: string;

    @HostListener('window:click', ['$event'])
    clickout(event: any): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.collapsed = true;
        }
    }

    ngOnInit(): void {
        this.getDropdownHeight();
    }

    /// <remarks>In case we are setting an event in the dropdown but cannot find it in the list, we will refresh the dropdown content(maybe stalled data).
    /// If we still cannot find it after that, it means that the event is expired. In this case we will unset the event from Video tab</remarks>
    ngOnChanges(changes: SimpleChanges): void {
        this.selectorTitle = this.eventsList.length ? this.sitecore.media.EventSelectorTitle : this.sitecore.media.NoLiveStreamingEvents;
        this.selectedEvent = null;
        if (this.selectedEventId === null) {
            return;
        }

        for (const sport of this.eventsList) {
            if (!sport.children) {
                continue;
            }

            const event = sport.children.find((child) => child.id === this.selectedEventId);
            if (event) {
                this.selectedEvent = { id: event.id.toString(), name: event.name, sportId: +sport.id };
                break;
            }
        }

        if (changes.selectedEventId) {
            if (this.selectedEvent === null) {
                this.fetchVideoEvents();
            }

            if (changes.selectedEventId.currentValue !== changes.selectedEventId.previousValue) {
                this.collapsed = true;
            }

            return;
        }

        if (!!this.selectedEventId && !this.selectedEvent) {
            this.unsetEvent();
        }
    }

    getDropdownHeight(): void {
        if (this.selectedEventId !== null) {
            //TODO any index issue, probably a defect, please investigate
            //@ts-ignore this looks wrong, below we use ColumnSize enum, but the map object is using RightColumnSize
            this.containerHeight = containerHeightMap[this.adaptiveLayoutService.current.rightColumnSize] || '';
        }
    }

    changed(event: EventSwitcherEventItem): void {
        this.toggleSelector();
        if (event.id !== this.selectedEventId) {
            this.trackingService.track(trackingConstants.EVENT_MEDIA, {
                [trackingConstants.COMPONENT_SITE_ELEMENT_INTERCATIONS]: 'MediaModule_EventSwitcher_EventChanged',
            });
            this.setDropdownEvent(event.id.toString());
        }
    }

    toggleSelector(): void {
        this.collapsed = !this.collapsed;
        this.getDropdownHeight();
    }

    scrolled(event: Event): void {
        event.stopPropagation();
    }

    private fetchVideoEvents(): void {
        this.store.dispatch(Actions.fetchVideoEvents());
    }

    private unsetEvent(): void {
        this.store.dispatch(Actions.unsetEvent({ columnType: EventDetailsColumnType.Video }));
    }

    private setDropdownEvent(eventId: string): void {
        this.store.dispatch(
            MediaApiActions.setEventId({
                payload: {
                    eventId,
                    source: MediaEventSource.EventSwitcher,
                    tab: EventDetailsColumnType.Video,
                    trackingVideoSource: TrackingVideoSource.EventSwitcher,
                },
            }),
        );
    }
}
